/**
 * Payment-Link Link-Tester — URL-Bau/-Parse-Helfer (Spec 501, Phase 3).
 *
 * Reine, format-agnostische URL-Werkzeuge. Alle Funktionen sind teaser-sicher:
 * sie rufen NIE `format.buildStringToSign` auf (das wirft im Teaser), sondern
 * nutzen nur `format.classifyParam` (liefert im Teaser `unknown`) und
 * `format.signatureParamName`/`available`. So bleibt die segmentierte Vorschau
 * sichtbar, ohne eine Signatur zu berechnen (Spec §Teaser-Verhalten).
 */
import type {
  BodyReach,
  LinkParam,
  PaymentLinkFormatDefinition,
  SignaturePlacement,
} from './format-definition';
import {
  detectStageFromUrl,
  isPlaceholderProd,
  type PayStage,
  type PayStageEntry,
} from './stage-map';
// Spec 516: Alias-Host-Erkennung (publicBaseUrl-Registry) im Analyse-Modus.
import { aliasEntryForOrigin } from './alias-hosts';

/** Ein Parameter-Segment der Vorschau, klassifiziert nach Body-Reach (Spec 503). */
export interface PreviewParam {
  key: string;
  value: string;
  /** Body-Reach bei geladenem Format, sonst `unknown` (Teaser). */
  reach: BodyReach | 'unknown';
}

/**
 * Segmentierte Live-URL-Vorschau (Spec 503 §2): Basis + die vier Body-Reach-Eimer
 * (systemField / meta / routing / raw) + Signatur. Unter „c=1" wird JEDER Parameter
 * außer `sig` signiert — es gibt KEINEN „unsigniert"-Eimer mehr; die Trennung ist
 * „erreicht den Facade-Body" statt „signiert/frei". `unknown` bleibt nur für den
 * Teaser (`PENDING_FORMAT` liefert keine Body-Reach-Auskunft).
 */
export interface UrlPreviewSegments {
  /** Basis-URL (gedaempft dargestellt). */
  base: string;
  /** Prod-Platzhalter → nicht-kopierbar, nie als echter Prod-Host darstellen. */
  basePlaceholder: boolean;
  /** System-Whitelist-Parameter (→ Body-Feld). */
  system: PreviewParam[];
  /** `meta_`-Parameter (→ `metadata.<x>`). */
  meta: PreviewParam[];
  /** Routing-Parameter `m`/`c` (signiert & Pflicht, kein Body-Feld). */
  routing: PreviewParam[];
  /** Rohe eigene Parameter (mitsigniert, erreichen die Facade nicht). */
  raw: PreviewParam[];
  /** Teaser-Eimer: ohne Body-Reach-Auskunft ist keine Zuordnung entscheidbar. */
  unknown: PreviewParam[];
  /** Signatur-Segment (gruen). */
  signature: {
    /** Name des Signatur-Parameters (`''` im Teaser). */
    paramName: string;
    /** Signaturwert; im Teaser leer. */
    value: string;
    /**
     * `true` = inerter Platzhalter (Teaser oder noch nicht berechnet) → NIE als
     * echte Signatur anzeigen (Spec §Teaser-Verhalten).
     */
    inert: boolean;
  };
}

/**
 * Baut die Vorschau-Segmente, gruppiert nach **Body-Reach** (Spec 503):
 * systemField / meta / routing / raw; `unknown` nur im Teaser. Die Zuordnung
 * kommt aus `format.bodyReachOf` — fehlt sie (Teaser/PENDING oder ein Format ohne
 * Body-Reach), landet der Parameter im `unknown`-Eimer. Die Signatur ist inert,
 * solange kein Format geladen ODER kein Signaturwert übergeben ist. Reihenfolge
 * innerhalb eines Eimers = Eingabereihenfolge.
 */
export function buildPreviewSegments(
  format: PaymentLinkFormatDefinition,
  baseEntry: PayStageEntry,
  inputs: { systemParams: LinkParam[]; freeParams: LinkParam[] },
  signatureValue?: string,
): UrlPreviewSegments {
  const system: PreviewParam[] = [];
  const meta: PreviewParam[] = [];
  const routing: PreviewParam[] = [];
  const raw: PreviewParam[] = [];
  const unknown: PreviewParam[] = [];
  for (const p of [...inputs.systemParams, ...inputs.freeParams]) {
    const reach = format.available ? format.bodyReachOf?.(p.key) : undefined;
    if (reach === undefined) {
      unknown.push({ key: p.key, value: p.value, reach: 'unknown' });
      continue;
    }
    const segment: PreviewParam = { key: p.key, value: p.value, reach };
    if (reach === 'systemField') {
      system.push(segment);
    } else if (reach === 'meta') {
      meta.push(segment);
    } else if (reach === 'routing') {
      routing.push(segment);
    } else {
      raw.push(segment);
    }
  }

  const inert = !format.available || !signatureValue;
  return {
    // Basis inkl. Klick-Pfad (Handover §1), damit die Vorschau dem Copy-Link gleicht.
    base: `${baseEntry.payBaseUrl}${format.clickPath ?? ''}`,
    basePlaceholder: isPlaceholderProd(baseEntry),
    system,
    meta,
    routing,
    raw,
    unknown,
    signature: {
      paramName: format.signatureParamName,
      value: inert ? '' : signatureValue!,
      inert,
    },
  };
}

/** URL-kodierte `k=v`-Kette aus Parametern. */
function encodeParams(params: LinkParam[]): string {
  return params
    .map((p) => `${encodeURIComponent(p.key)}=${encodeURIComponent(p.value)}`)
    .join('&');
}

/**
 * Baut den vollstaendigen (kopierbaren) Link. Die Platzierung des Signatur-
 * Parameters folgt der Format-Definition (`signaturePlacement`): `query-append`
 * haengt sie als letzten, `query-prepend` als ersten Query-Parameter an. So bleibt
 * der Helfer beim echten Format-Einstecken unveraendert (Phase-1-Naht). Nur
 * sinnvoll bei geladenem Format + berechneter Signatur; der Aufrufer sperrt Copy
 * im Teaser/Prod-Platzhalter (`isCopyableLink`).
 */
export function buildFullUrl(
  baseEntry: PayStageEntry,
  params: LinkParam[],
  signatureParamName: string,
  signatureValue: string,
  placement: SignaturePlacement = 'query-append',
  clickPath = '',
): string {
  const sigParam: LinkParam[] =
    signatureParamName && signatureValue
      ? [{ key: signatureParamName, value: signatureValue }]
      : [];
  // `query-prepend` = Signatur zuerst; sonst (append / unknown-Fallback) zuletzt.
  const all =
    placement === 'query-prepend'
      ? [...sigParam, ...params]
      : [...params, ...sigParam];
  const query = encodeParams(all);
  // Der Klick-Pfad (`/link/v1`) hängt an die Stage-Basis, damit der Link routet.
  const base = `${baseEntry.payBaseUrl}${clickPath}`;
  return query ? `${base}?${query}` : base;
}

/**
 * Darf der Gesamtlink kopiert werden? Nur bei geladenem Format, echter Signatur
 * und NICHT-Platzhalter-Basis (Prod-Platzhalter sperrt den Copy — Spec §Scope 6).
 */
export function isCopyableLink(
  format: PaymentLinkFormatDefinition,
  baseEntry: PayStageEntry,
  signatureValue?: string,
): boolean {
  return (
    format.available &&
    !isPlaceholderProd(baseEntry) &&
    Boolean(signatureValue)
  );
}

/** Maximale roh-Query-Länge in Bytes (Handover §5 — darüber lehnt der Dienst ab). */
export const MAX_QUERY_BYTES = 8192;

/** Ergebnis des Parse einer eingefuegten URL (Modus B). */
export type ParsedLink =
  | { ok: false; reason: 'empty' | 'notParseable' }
  | {
      ok: true;
      baseUrl: string;
      /** Alle Query-Parameter AUSSER dem Signatur-Parameter (falls erkennbar). */
      params: LinkParam[];
      /** Signaturwert, falls das Format den Signatur-Parameter benennt (sonst undefined). */
      signature?: string;
      /** Keys, die mehrfach vorkommen (Spec-Edge-Case doppelte Query-Parameter). */
      duplicates: string[];
      /**
       * `true` = das Format benennt einen Signatur-Parameter, der in der URL fehlt.
       * `undefined` = im Teaser nicht entscheidbar (kein Signatur-Parametername).
       */
      missingSignature?: boolean;
      /** Stage der URL bzw. `null`, wenn keiner bekannten Stage zuordenbar. */
      stage: PayStage | null;
      /** `true` = Origin weder Stage-Base noch bekannter Alias-Host (Spec 516). */
      unknownStage: boolean;
      /**
       * `true` = Origin ist ein registrierter Merchant-Alias-Host
       * (`publicBaseUrl`-Registry, Spec 516) — `stage` kommt dann aus der
       * Registry statt aus der `pay.<stage>`-Map.
       */
      aliasHost: boolean;
      /**
       * Keys, deren (dekodierter) Wert ein Steuerzeichen (`\n`/`\r`/Null u. a.)
       * enthält → vom Dienst abgelehnt (Handover §5).
       */
      controlCharKeys: string[];
      /**
       * Roh-Query-Segmente OHNE `=` (z. B. `foo` statt `foo=`) → abgelehnt
       * (Handover §5; ein leerer Wert `key=` ist dagegen erlaubt).
       */
      segmentsWithoutEquals: string[];
      /** Roh-Query > `MAX_QUERY_BYTES` → Warnung (Handover §5). */
      tooLong: boolean;
    };

/**
 * Parst eine eingefuegte URL teaser-sicher: extrahiert Parameter, erkennt
 * doppelte Keys, unbekannte Stage und (nur bei geladenem Format) den fehlenden
 * Signatur-Parameter. Nie werfend — jede Fehl-Eingabe ergibt einen definierten
 * Zustand (Spec §Edge-Cases Modus B).
 */
export function parsePastedUrl(
  raw: string,
  format: PaymentLinkFormatDefinition,
): ParsedLink {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return { ok: false, reason: 'empty' };
  }

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return { ok: false, reason: 'notParseable' };
  }

  // Doppelte Keys ueber die rohen Query-Paare zaehlen (searchParams.get() wuerde
  // Duplikate verschlucken).
  const counts = new Map<string, number>();
  const allPairs: LinkParam[] = [];
  for (const [key, value] of url.searchParams.entries()) {
    counts.set(key, (counts.get(key) ?? 0) + 1);
    allPairs.push({ key, value });
  }
  const duplicates = [...counts.entries()]
    .filter(([, n]) => n > 1)
    .map(([k]) => k);

  // Randfall-Validierung auf der ROHEN Query (Handover §5) — `searchParams` sieht
  // weder Segmente ohne `=` (macht daraus key mit Leerwert) noch die Byte-Länge.
  const rawQuery = url.search.startsWith('?')
    ? url.search.slice(1)
    : url.search;
  const tooLong = new TextEncoder().encode(rawQuery).length > MAX_QUERY_BYTES;
  const segmentsWithoutEquals = rawQuery
    .split('&')
    .filter((seg) => seg !== '' && !seg.includes('='));
  // Steuerzeichen in den DEKODIERTEN Werten (z. B. `%0A` → `\n`).
  const controlCharKeys = allPairs
    // eslint-disable-next-line no-control-regex
    .filter((p) => /[\u0000-\u001F\u007F]/.test(p.value))
    .map((p) => p.key);

  const sigName = format.available ? format.signatureParamName : '';
  let signature: string | undefined;
  let missingSignature: boolean | undefined;
  let params = allPairs;
  if (sigName) {
    const sigPair = allPairs.find((p) => p.key === sigName);
    signature = sigPair?.value;
    missingSignature = sigPair === undefined;
    params = allPairs.filter((p) => p.key !== sigName);
  }

  // Stage-Map-Hoheit bleibt bei `detectStageFromUrl`; die Alias-Registry wird
  // NUR als Zusatz konsultiert (Spec 516): ein registrierter Merchant-Alias-Host
  // wird seiner Stage zugeordnet statt als unbekannter Host diagnostiziert.
  const stageFromMap = detectStageFromUrl(trimmed);
  const aliasEntry = stageFromMap === null ? aliasEntryForOrigin(trimmed) : null;
  const stage = stageFromMap ?? aliasEntry?.stage ?? null;
  return {
    ok: true,
    baseUrl: url.origin + url.pathname,
    params,
    signature,
    duplicates,
    missingSignature,
    stage,
    unknownStage: stage === null,
    aliasHost: aliasEntry !== null,
    controlCharKeys,
    segmentsWithoutEquals,
    tooLong,
  };
}
